<template>
  <div class="post-feed-page">
    <el-row :gutter="16" class="feed-row">
      <!-- 左：话题导航 -->
      <el-col :xs="0" :sm="0" :md="4" class="feed-col-left">
        <div class="feed-sticky">
          <TopicSidebar
            :topics="topics"
            :feed-mode="feedMode"
            :topic-id="currentTopicId"
            @select="handleSidebarSelect"
          />
        </div>
      </el-col>

      <!-- 中：发布 + 动态 -->
      <el-col :xs="24" :sm="24" :md="14" class="feed-col-main">
        <nav
          ref="mobileTabsRef"
          class="feed-mobile-tabs layout-show-md-down"
          aria-label="动态分类"
        >
          <button
            type="button"
            class="feed-mobile-tab"
            :class="{ 'is-active': mobileTabKey === 'latest' }"
            data-tab-key="latest"
            @click="handleMobileTab('latest')"
          >
            最新
          </button>
          <button
            type="button"
            class="feed-mobile-tab"
            :class="{ 'is-active': mobileTabKey === 'hot' }"
            data-tab-key="hot"
            @click="handleMobileTab('hot')"
          >
            热门
          </button>
          <button
            v-for="t in topics"
            :key="t.id"
            type="button"
            class="feed-mobile-tab"
            :class="{ 'is-active': mobileTabKey === `topic-${t.id}` }"
            :data-tab-key="`topic-${t.id}`"
            @click="handleMobileTab(`topic-${t.id}`)"
          >
            {{ t.name }}
          </button>
        </nav>

        <PostComposer
          id="post-composer"
          class="feed-composer-desktop"
          :topics="topics"
          @need-login="openLogin"
          @published="reloadFeed"
        />

        <div
          v-loading="loading"
          class="feed-list"
          :class="{ 'feed-list--placeholder': loading && postList.length === 0 }"
        >
          <PostFeedItem
            v-for="post in postList"
            :key="post.id"
            :post="post"
            @need-login="openLogin"
            @open-profile="openUserProfile"
            @select-topic="selectTopic"
            @comment-count-change="(n) => (post.commentCount = n)"
            @deleted="handlePostDeleted"
          />

          <el-empty
            v-if="!loading && postList.length === 0"
            description="暂无动态"
            :image-size="80"
          />

          <div v-if="postList.length > 0" class="feed-load-more">
            <el-button
              v-if="hasMore"
              :loading="loadingMore"
              text
              @click="loadPosts(true)"
            >
              加载更多
            </el-button>
            <span v-else class="feed-load-more-tip">没有更多了</span>
          </div>
        </div>
      </el-col>

      <!-- 右：签到 / 热门等，随中间内容一起滚动 -->
      <el-col :xs="24" :sm="24" :md="6" class="feed-col-right">
        <div class="feed-aside">
          <DailyCheckin
            :is-login="isLogin"
            @to-login="openLogin"
            @checkin-success="$refs.checkinRankRef.load()"
          />
          <CheckinRank ref="checkinRankRef" />
          <AliyunPromo />
          <HotTopics :topics="hotTopics" @select="selectTopic" />
          <FriendLinks />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getPostPage, getTopicAll } from "@/api/post";
import { scrollToPostComposer } from "@/utils/scroll";
import PostComposer from "@/components/post/PostComposer.vue";
import PostFeedItem from "@/components/post/PostFeedItem.vue";
import TopicSidebar from "@/components/post/TopicSidebar.vue";
import HotTopics from "@/components/post/HotTopics.vue";
import DailyCheckin from "@/components/DailyCheckin.vue";
import CheckinRank from "@/components/CheckinRank.vue";
import AliyunPromo from "@/components/AliyunPromo.vue";
import FriendLinks from "@/components/FriendLinks.vue";

export default {
  name: "PostView",
  components: {
    PostComposer,
    PostFeedItem,
    TopicSidebar,
    HotTopics,
    DailyCheckin,
    CheckinRank,
    AliyunPromo,
    FriendLinks,
  },
  inject: {
    layoutRef: {
      default: () => ({
        isLogin: false,
        openLoginDialog: () => {},
        userInfo: {},
      }),
    },
  },
  data() {
    return {
      topics: [],
      feedMode: "latest",
      currentTopicId: null,
      postList: [],
      loading: false,
      loadingMore: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
    };
  },
  computed: {
    hasMore() {
      return this.total > 0 && this.postList.length < this.total;
    },
    hotTopics() {
      return [...this.topics]
        .sort((a, b) => {
          const hb = Number(b?.heat) || 0;
          const ha = Number(a?.heat) || 0;
          if (hb !== ha) return hb - ha;
          return (Number(a?.sort) || 0) - (Number(b?.sort) || 0);
        })
        .slice(0, 10);
    },
    isLogin() {
      return this.layoutRef?.isLogin === true;
    },
    mobileTabKey() {
      if (this.currentTopicId != null && this.currentTopicId !== "") {
        return `topic-${this.currentTopicId}`;
      }
      return this.feedMode === "hot" ? "hot" : "latest";
    },
  },
  mounted() {
    this.loadTopics();
    this.loadPosts();
    this._onPostPublished = () => this.reloadFeed();
    window.addEventListener("post-published", this._onPostPublished);
    this.$nextTick(() => this.handleComposeHash());
  },
  beforeUnmount() {
    if (this._onPostPublished) {
      window.removeEventListener("post-published", this._onPostPublished);
    }
  },
  watch: {
    "$route.hash"(hash) {
      if (hash === "#compose") this.handleComposeHash();
    },
  },
  methods: {
    openLogin() {
      this.layoutRef?.openLoginDialog?.("login");
    },
    handleComposeHash() {
      if (this.$route.hash !== "#compose") return;
      const isMobile =
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
      if (isMobile) {
        this.layoutRef?.openPublishComposer?.();
        this.$router.replace({ path: "/", hash: "" });
        return;
      }
      this.$nextTick(() => {
        scrollToPostComposer();
        this.$router.replace({ path: "/", hash: "" });
      });
    },
    async loadTopics() {
      try {
        const res = await getTopicAll();
        if (res?.data?.code === 200) {
          this.topics = res.data.data || [];
          this.$nextTick(() => this.scrollMobileTabIntoView());
        }
      } catch (e) {
        console.error("加载话题失败:", e);
      }
    },
    async loadPosts(append = false) {
      if (append) {
        if (!this.hasMore || this.loadingMore || this.loading) return;
        this.loadingMore = true;
        this.currentPage += 1;
      } else {
        this.loading = true;
        this.currentPage = 1;
      }
      try {
        const res = await getPostPage(
          this.currentPage,
          this.pageSize,
          this.currentTopicId,
          this.feedMode
        );
        if (res?.data?.code === 200) {
          const pageData = res.data.data || {};
          const newList = pageData.records || [];
          this.postList = append ? [...this.postList, ...newList] : newList;
          this.total = pageData.total || 0;
        }
      } catch (e) {
        console.error("加载动态失败:", e);
        if (append) this.currentPage -= 1;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    reloadFeed() {
      this.loadTopics();
      this.loadPosts(false);
    },
    handleSidebarSelect({ mode, topicId }) {
      this.feedMode = mode || "latest";
      this.currentTopicId = topicId ?? null;
      this.loadPosts(false);
      this.$nextTick(() => this.scrollMobileTabIntoView());
    },
    handleMobileTab(key) {
      if (key === "latest") {
        this.handleSidebarSelect({ mode: "latest", topicId: null });
        return;
      }
      if (key === "hot") {
        this.handleSidebarSelect({ mode: "hot", topicId: null });
        return;
      }
      if (String(key).startsWith("topic-")) {
        const id = Number(String(key).slice(6));
        if (Number.isFinite(id)) {
          this.handleSidebarSelect({ mode: "latest", topicId: id });
        }
      }
    },
    scrollMobileTabIntoView() {
      const root = this.$refs.mobileTabsRef;
      if (!root) return;
      const active = root.querySelector(".feed-mobile-tab.is-active");
      if (!active) return;
      active.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    },
    selectTopic(id) {
      this.handleSidebarSelect({ mode: "latest", topicId: id });
    },
    openUserProfile(userId) {
      if (userId == null || userId === "") return;
      const route = this.$router.resolve({ path: `/profile/${userId}` });
      window.open(route.href, "_blank");
    },
    handlePostDeleted(postId) {
      this.postList = this.postList.filter((p) => Number(p.id) !== Number(postId));
      this.loadTopics();
    },
  },
};
</script>

<style scoped>
.post-feed-page {
  min-height: 60vh;
}

/* 三栏等高，左侧 sticky 才能贴顶 */
.feed-row {
  align-items: stretch;
}

/* 左侧话题贴顶 */
.feed-sticky {
  position: sticky;
  top: 75px;
  z-index: 1;
  display: flow-root;
}

/* 右侧随中间内容滚动 */
.feed-aside {
  display: flow-root;
}

.feed-list--placeholder {
  min-height: 360px;
}

.feed-load-more {
  display: flex;
  justify-content: center;
  padding: 8px 0 24px;
}

.feed-load-more-tip {
  color: var(--el-text-color-secondary);
  font-size: 13px;
}

.feed-mobile-tabs {
  display: none;
  align-items: center;
  gap: 4px;
  margin: 0 -4px 12px;
  padding: 0 4px 2px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-bottom: 1px solid var(--el-border-color-lighter);
  background: var(--el-bg-color-page);
}

.feed-mobile-tabs::-webkit-scrollbar {
  display: none;
}

.feed-mobile-tab {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.2;
  color: var(--el-text-color-regular);
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.feed-mobile-tab.is-active {
  color: var(--el-color-primary);
  font-weight: 600;
}

.feed-mobile-tab.is-active::after {
  content: "";
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 0;
  height: 2px;
  border-radius: 1px;
  background: var(--el-color-primary);
}

.layout-show-md-down {
  display: none !important;
}

@media (max-width: 992px) {
  .feed-col-left {
    display: none !important;
  }

  .feed-sticky {
    position: static;
  }

  .layout-show-md-down {
    display: flex !important;
  }

  .feed-mobile-tabs {
    position: sticky;
    top: 60px;
    z-index: 8;
  }
}

@media (max-width: 768px) {
  .feed-composer-desktop {
    display: none !important;
  }
}
</style>
